import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { useGenerators } from '../hooks/useGenerators';
import { useSubmissions, STATUS_VALUES, STATUS_LABELS } from '../hooks/useSubmissions';
import type { Submission, SubmissionStatus } from '../hooks/useSubmissions';
import { SubmissionDetail } from '../components/leads/SubmissionDetail';
import { DashboardLayout } from '../components/DashboardLayout';
import { formatPrice } from '../lib/pricing';
import { isGeneratorConfigV1 } from '../types/generator-config';
import type { GeneratorConfigV1 } from '../types/generator-config';

export function LeadsPage() {
    const { id: generatorIdParam } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const { org } = useCurrentOrg();
    const { generators } = useGenerators(org?.id);
    const { submissions, loading, error, updateStatus } = useSubmissions({
        organizationId: org?.id,
        generatorId: generatorIdParam,
    });

    const [selected, setSelected] = useState<Submission | null>(null);
    const [statusFilter, setStatusFilter] = useState<SubmissionStatus | 'all'>('all');
    const [query, setQuery] = useState('');

    const selectedGenerator = useMemo(
        () => generators.find((g) => g.id === generatorIdParam) ?? null,
        [generators, generatorIdParam]
    );

    const genById = useMemo(() => {
        const map: Record<string, { name: string; config: GeneratorConfigV1 | null }> = {};
        for (const g of generators) {
            map[g.id] = {
                name: g.name,
                config: isGeneratorConfigV1(g.config) ? (g.config as GeneratorConfigV1) : null,
            };
        }
        return map;
    }, [generators]);

    const filtered = useMemo(() => {
        let list = submissions;
        if (statusFilter !== 'all') list = list.filter((s) => s.status === statusFilter);
        if (query.trim()) {
            const q = query.toLowerCase();
            list = list.filter((s) =>
                (s.contact_name ?? '').toLowerCase().includes(q) ||
                (s.contact_email ?? '').toLowerCase().includes(q) ||
                (s.contact_phone ?? '').toLowerCase().includes(q)
            );
        }
        return list;
    }, [submissions, statusFilter, query]);

    useEffect(() => {
        setSelected(null);
    }, [generatorIdParam]);

    const handleStatusChange = async (id: string, status: SubmissionStatus) => {
        await updateStatus(id, status);
        setSelected((prev) => (prev && prev.id === id ? { ...prev, status } : prev));
    };

    return (
        <DashboardLayout>
            <main className="dash-main">
                <section className="leads-head">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => navigate(selectedGenerator ? `/generator/${selectedGenerator.id}/edit` : '/dashboard')}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        {selectedGenerator ? 'Retour à l\'éditeur' : 'Retour au dashboard'}
                    </button>
                    <div>
                        <span className="dash-eyebrow">
                            {selectedGenerator ? selectedGenerator.name : 'Toutes les demandes'}
                        </span>
                        <h1>Demandes reçues</h1>
                        <p>
                            {filtered.length} demande{filtered.length > 1 ? 's' : ''}
                            {selectedGenerator ? ` sur ce générateur` : ` sur l'ensemble de vos générateurs`}.
                        </p>
                    </div>
                </section>

                <section className="leads-toolbar">
                    <div className="leads-filters">
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Rechercher par nom, email, téléphone…"
                            className="leads-search"
                        />
                        <div className="leads-status-filter">
                            <button
                                type="button"
                                className={`chip-filter${statusFilter === 'all' ? ' is-active' : ''}`}
                                onClick={() => setStatusFilter('all')}
                            >
                                Toutes
                            </button>
                            {STATUS_VALUES.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    className={`chip-filter${statusFilter === s ? ' is-active' : ''}`}
                                    onClick={() => setStatusFilter(s)}
                                >
                                    {STATUS_LABELS[s]}
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                {error && (
                    <div className="auth-error" role="alert" style={{ marginBottom: 16 }}>
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="dash-loading">
                        <div className="spinner" aria-hidden />
                        <span>Chargement des demandes…</span>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="leads-empty">
                        <h2>Aucune demande pour l'instant</h2>
                        <p>
                            Dès qu'un visiteur remplira le formulaire de votre générateur publié, la demande apparaîtra ici.
                        </p>
                    </div>
                ) : (
                    <section className="leads-table-wrap">
                        <table className="leads-table">
                            <thead>
                                <tr>
                                    <th>Contact</th>
                                    {!generatorIdParam && <th>Générateur</th>}
                                    <th>Estimation</th>
                                    <th>Reçu le</th>
                                    <th>Statut</th>
                                    <th aria-label="Actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((s) => {
                                    const gen = genById[s.generator_id];
                                    const currency = gen?.config?.branding.currency ?? 'EUR';
                                    return (
                                        <tr
                                            key={s.id}
                                            className="leads-row"
                                            onClick={() => setSelected(s)}
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') setSelected(s);
                                            }}
                                        >
                                            <td>
                                                <div className="leads-name">{s.contact_name || '—'}</div>
                                                <div className="leads-sub">
                                                    {[s.contact_email, s.contact_phone].filter(Boolean).join(' · ') || '—'}
                                                </div>
                                            </td>
                                            {!generatorIdParam && (
                                                <td className="leads-gen-name">{gen?.name ?? '—'}</td>
                                            )}
                                            <td>
                                                {s.estimated_total != null
                                                    ? formatPrice(s.estimated_total, currency)
                                                    : '—'}
                                            </td>
                                            <td className="leads-date">
                                                {new Date(s.created_at).toLocaleString('fr-FR', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </td>
                                            <td>
                                                <span className={`sub-status-pill sub-status-${s.status}`}>
                                                    {STATUS_LABELS[s.status as SubmissionStatus] ?? s.status}
                                                </span>
                                            </td>
                                            <td className="leads-action">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                                    <polyline points="9 18 15 12 9 6" />
                                                </svg>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                )}
            </main>

            {selected && (
                <SubmissionDetail
                    submission={selected}
                    config={genById[selected.generator_id]?.config ?? null}
                    currency={genById[selected.generator_id]?.config?.branding.currency ?? 'EUR'}
                    onClose={() => setSelected(null)}
                    onUpdateStatus={(status) => void handleStatusChange(selected.id, status)}
                />
            )}
        </DashboardLayout>
    );
}
